import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export function slugToTabIndex(
  slug: string | null,
  slugs: readonly string[],
): number {
  if (slug) {
    const idx = slugs.indexOf(slug);
    if (idx >= 0) return idx;
  }
  return 0;
}

export function tabIndexToSlug(
  index: number,
  slugs: readonly string[],
): string {
  return slugs[index] ?? slugs[0];
}

export interface UseBookmarkableTabOptions {
  slugs: readonly string[];
  paramName?: string;
}

export function useBookmarkableTab({
  slugs,
  paramName = "tab",
}: UseBookmarkableTabOptions) {
  const [searchParams, setSearchParams] = useSearchParams();

  const tabIndex = useMemo(
    () => slugToTabIndex(searchParams.get(paramName), slugs),
    [searchParams, paramName, slugs],
  );

  const activeSlug = tabIndexToSlug(tabIndex, slugs);

  const setTabIndex = useCallback(
    (_: React.SyntheticEvent, newIndex: number) => {
      const slug = tabIndexToSlug(newIndex, slugs);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(paramName, slug);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, slugs, paramName],
  );

  return { tabIndex, setTabIndex, activeSlug };
}
